import { apiFetch, apiJson } from "@/shared/api-client";
import type { Me } from "@/shared/auth/types";
import { meSchema } from "../schemas";

/** Pure async functions over apiFetch. No React, no hooks, no controller. */
export const authApi = {
  /** Which sign-in providers this deployment has set up. Needs no session. */
  async signInProviders(signal?: AbortSignal): Promise<SignInProviders> {
    return apiJson<SignInProviders>("/api/v1/auth/providers", { signal });
  },

  async me(signal?: AbortSignal): Promise<Me> {
    const raw = await apiJson<unknown>("/api/v1/auth/me", { signal });
    return meSchema.parse(raw);
  },

  async updateProfile(args: { display_name: string }): Promise<Me> {
    const raw = await apiJson<unknown>("/api/v1/auth/me", {
      method: "PATCH",
      body: JSON.stringify(args),
    });
    return meSchema.parse(raw);
  },

  async logout(): Promise<void> {
    await apiFetch("/api/v1/auth/logout", { method: "POST" });
  },

  async forgotPassword(email: string): Promise<void> {
    // Always 200 by design (email-enumeration discipline); we don't branch.
    await apiFetch("/api/v1/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(
    args:
      | { email: string; code: string; new_password: string }
      | { challenge_id: string; token: string; new_password: string },
  ): Promise<void> {
    await apiFetch("/api/v1/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async changePassword(args: { current_password: string; new_password: string }): Promise<void> {
    await apiJson("/api/v1/auth/change-password", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async changeEmailStart(args: { new_email: string; current_password: string }): Promise<void> {
    await apiJson("/api/v1/auth/change-email", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async changeEmailConfirm(code: string): Promise<{ email: string }> {
    return apiJson<{ email: string }>("/api/v1/auth/change-email/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  async deleteAccount(currentPassword: string): Promise<void> {
    await apiJson("/api/v1/auth/delete-account", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword }),
    });
  },

  async verifyEmail(code: string): Promise<void> {
    await apiJson("/api/v1/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  async verifyEmailLink(args: { challenge_id: string; token: string }): Promise<void> {
    await apiJson("/api/v1/auth/verify-email/link", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async resendVerification(): Promise<void> {
    await apiJson("/api/v1/auth/resend-verification", { method: "POST" });
  },

  async sendMagicLink(email: string): Promise<void> {
    // Always 200 by design (email-enumeration discipline).
    await apiFetch("/api/v1/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  // ── Sessions (devices) ────────────────────────────────────────────

  async listSessions(signal?: AbortSignal): Promise<SessionsResponse> {
    return apiJson<SessionsResponse>("/api/v1/auth/sessions", { signal });
  },

  async revokeSession(chainId: string): Promise<void> {
    await apiJson(`/api/v1/auth/sessions/${chainId}`, { method: "DELETE" });
  },

  async logoutAll(): Promise<void> {
    await apiJson("/api/v1/auth/logout-all", { method: "POST" });
  },

  // ── MFA ───────────────────────────────────────────────────────────

  async mfaStatus(signal?: AbortSignal): Promise<MfaStatus> {
    return apiJson<MfaStatus>("/api/v1/auth/mfa/", { signal });
  },

  async mfaEnroll(method: "totp" | "email" | "sms"): Promise<MfaEnrollResponse> {
    return apiJson<MfaEnrollResponse>("/api/v1/auth/mfa/enroll", {
      method: "POST",
      body: JSON.stringify({ method }),
    });
  },

  async mfaConfirm(code: string): Promise<{ recovery_codes: string[] }> {
    return apiJson<{ recovery_codes: string[] }>("/api/v1/auth/mfa/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  async mfaDisable(args: { method: string; code: string }): Promise<void> {
    await apiJson("/api/v1/auth/mfa/disable", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async mfaRegenerateRecovery(code: string): Promise<{ recovery_codes: string[] }> {
    return apiJson<{ recovery_codes: string[] }>("/api/v1/auth/mfa/recovery-codes/regenerate", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  // ── Passkeys ──────────────────────────────────────────────────────

  async webauthnRegisterBegin(): Promise<unknown> {
    return apiJson<unknown>("/api/v1/auth/webauthn/register/begin", { method: "POST" });
  },

  async webauthnRegisterFinish(payload: unknown): Promise<void> {
    await apiJson("/api/v1/auth/webauthn/register/finish", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async webauthnList(signal?: AbortSignal): Promise<PasskeyListResponse> {
    return apiJson<PasskeyListResponse>("/api/v1/auth/webauthn/credentials", { signal });
  },

  async webauthnDelete(passkeyId: string): Promise<void> {
    await apiJson(`/api/v1/auth/webauthn/credentials/${passkeyId}`, { method: "DELETE" });
  },

  // ── Phone ─────────────────────────────────────────────────────────

  async phoneEnroll(phone: string): Promise<{ challenge_id: string }> {
    return apiJson<{ challenge_id: string }>("/api/v1/auth/phone/enroll", {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
  },

  async phoneEnrollVerify(args: { challenge_id: string; code: string }): Promise<void> {
    await apiJson("/api/v1/auth/phone/enroll/verify", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async phoneRemove(): Promise<void> {
    await apiJson("/api/v1/auth/phone/", { method: "DELETE" });
  },

  async mfaRequestCode(args: { challenge_id: string; method: string }): Promise<void> {
    await apiJson("/api/v1/auth/mfa/challenge", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },
};

export interface SignInProviders {
  github: boolean;
  google: boolean;
  microsoft: boolean;
}

export interface DeviceSession {
  chain_id: string;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
  last_seen_at: string;
  current: boolean;
}

export interface SessionsResponse {
  sessions: DeviceSession[];
  total: number;
}

export interface MfaStatus {
  methods: string[];
  recovery_codes_remaining: number;
}

export interface MfaEnrollResponse {
  method: string;
  secret?: string | null;
  otpauth_uri?: string | null;
}

export interface Passkey {
  id: string;
  nickname: string;
  transports: string;
  created_at: string;
  last_used_at: string | null;
  flagged: boolean;
}

export interface PasskeyListResponse {
  passkeys: Passkey[];
  total: number;
}
